/**
 * FASE 1B - Prima Nota
 * Registro cronologico con saldo progressivo
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Book, Euro, Search, Filter, Calendar, ChevronLeft, ChevronRight,
  ArrowUpCircle, ArrowDownCircle, Download, RefreshCw, ExternalLink,
  FileText, Building, TrendingUp, TrendingDown
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' }
];

export default function EconomicPrimaNota() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 100, total: 0, pages: 0 });
  const [units, setUnits] = useState([]);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterStruttura, setFilterStruttura] = useState('');
  const [filterCanale, setFilterCanale] = useState('');
  const [filterFornitore, setFilterFornitore] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  const fetchPrimaNota = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        anno: selectedYear,
        mese: selectedMonth,
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (filterCategoria) params.append('categoria', filterCategoria);
      if (filterStruttura) params.append('struttura', filterStruttura);
      if (filterCanale) params.append('canale', filterCanale);
      if (filterFornitore) params.append('fornitore', filterFornitore);
      if (filterTipo) params.append('tipo', filterTipo);
      
      const response = await axios.get(
        `${API}/admin/economic/prima-nota?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setData(response.data);
      setPagination(response.data.pagination || { page: 1, limit: 100, total: 0, pages: 0 });
    } catch (error) {
      console.error('Error fetching prima nota:', error);
      toast.error('Errore nel caricamento della Prima Nota');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth, pagination.page, pagination.limit, filterCategoria, filterStruttura, filterCanale, filterFornitore, filterTipo]);

  const fetchUnits = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/admin/economic/units`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUnits(response.data.units || []);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPrimaNota();
      fetchUnits();
    }
  }, [token, fetchPrimaNota, fetchUnits]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.length === 7) {
      const [y, m] = dateStr.split('-');
      return `${MONTHS[parseInt(m) - 1]?.label || m} ${y}`;
    }
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const exportCSV = () => {
    if (!data?.movimenti?.length) {
      toast.error('Nessun dato da esportare');
      return;
    }
    
    const headers = ['Data', 'Tipo', 'Descrizione', 'Categoria', 'Entrata', 'Uscita', 'Saldo', 'Stato'];
    const rows = data.movimenti.map(m => [
      m.data,
      m.tipo_movimento,
      m.descrizione,
      m.categoria,
      m.entrata || '',
      m.uscita || '',
      m.saldo_progressivo,
      m.stato
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(r => r.join(';'))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `prima_nota_${selectedYear}_${selectedMonth}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('CSV esportato');
  };

  return (
    <AdminLayout title="Prima Nota">
      <div className="space-y-4 pb-20 md:pb-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Prima Nota</h1>
            <p className="text-sm text-gray-500">Registro cronologico con saldo progressivo</p>
          </div>
          
          {/* Month Navigator */}
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1 w-full md:w-auto justify-center">
            <Button variant="ghost" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[140px] justify-center">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border-0 bg-transparent font-medium text-center appearance-none cursor-pointer text-sm"
              >
                {MONTHS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border-0 bg-transparent text-gray-500 appearance-none cursor-pointer text-sm"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button variant="ghost" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Totale Entrate</p>
              <p className="text-lg md:text-xl font-bold text-emerald-600">{formatCurrency(data?.totale_entrate)}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Totale Uscite</p>
              <p className="text-lg md:text-xl font-bold text-red-600">{formatCurrency(data?.totale_uscite)}</p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${(data?.saldo_finale || 0) >= 0 ? 'border-l-green-500' : 'border-l-red-500'} col-span-2 md:col-span-2`}>
            <CardContent className="p-3">
              <p className="text-xs text-gray-500 uppercase">Saldo Finale</p>
              <p className={`text-xl md:text-2xl font-bold ${(data?.saldo_finale || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(data?.saldo_finale)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex gap-2 flex-wrap flex-1">
            <select
              value={filterTipo}
              onChange={(e) => setFilterTipo(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
            >
              <option value="">Tutti i movimenti</option>
              <option value="entrata">Solo Entrate</option>
              <option value="uscita">Solo Uscite</option>
            </select>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-1" />
              Filtri
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchPrimaNota}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
          </div>
        </div>

        {/* Extended Filters */}
        {showFilters && (
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Categoria</label>
                <Input
                  placeholder="Categoria..."
                  value={filterCategoria}
                  onChange={(e) => setFilterCategoria(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Struttura</label>
                <select
                  value={filterStruttura}
                  onChange={(e) => setFilterStruttura(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="">Tutte</option>
                  {units.map(u => (
                    <option key={u.id} value={u.id}>{u.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Canale</label>
                <Input
                  placeholder="Canale..."
                  value={filterCanale}
                  onChange={(e) => setFilterCanale(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Fornitore</label>
                <Input
                  placeholder="Fornitore..."
                  value={filterFornitore}
                  onChange={(e) => setFilterFornitore(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterCategoria('');
                    setFilterStruttura('');
                    setFilterCanale('');
                    setFilterFornitore('');
                    setFilterTipo('');
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Prima Nota Table / Cards */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : !data?.movimenti?.length ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Book className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Nessun movimento nel periodo selezionato</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block">
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b sticky top-0">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Data</th>
                          <th className="text-left p-3 font-medium text-gray-600">Tipo</th>
                          <th className="text-left p-3 font-medium text-gray-600">Descrizione</th>
                          <th className="text-left p-3 font-medium text-gray-600">Categoria</th>
                          <th className="text-left p-3 font-medium text-gray-600">Canale/Fornitore</th>
                          <th className="text-right p-3 font-medium text-gray-600">Entrata</th>
                          <th className="text-right p-3 font-medium text-gray-600">Uscita</th>
                          <th className="text-right p-3 font-medium text-gray-600 bg-gray-100">Saldo</th>
                          <th className="text-center p-3 font-medium text-gray-600">Stato</th>
                          <th className="text-center p-3 font-medium text-gray-600"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.movimenti.map((mov, idx) => (
                          <tr key={mov.id || idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 text-gray-600 whitespace-nowrap">{formatDate(mov.data)}</td>
                            <td className="p-3">
                              {mov.tipo_movimento === 'entrata' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                  <ArrowUpCircle className="w-3 h-3 mr-1" />
                                  Entrata
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                  <ArrowDownCircle className="w-3 h-3 mr-1" />
                                  Uscita
                                </span>
                              )}
                            </td>
                            <td className="p-3 max-w-[250px]">
                              <div className="font-medium text-gray-800 truncate" title={mov.descrizione}>
                                {mov.descrizione}
                              </div>
                              {mov.codice_prenotazione && (
                                <div className="text-xs text-gray-500">{mov.codice_prenotazione}</div>
                              )}
                            </td>
                            <td className="p-3">
                              <span className="capitalize text-gray-600">{mov.categoria}</span>
                            </td>
                            <td className="p-3 text-gray-500">
                              {mov.canale || mov.fornitore || '-'}
                            </td>
                            <td className="p-3 text-right">
                              {mov.entrata ? (
                                <span className="font-medium text-emerald-600">+{formatCurrency(mov.entrata)}</span>
                              ) : '-'}
                            </td>
                            <td className="p-3 text-right">
                              {mov.uscita ? (
                                <span className="font-medium text-red-600">-{formatCurrency(mov.uscita)}</span>
                              ) : '-'}
                            </td>
                            <td className={`p-3 text-right font-bold bg-gray-50 ${mov.saldo_progressivo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(mov.saldo_progressivo)}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-0.5 rounded-full text-xs ${
                                mov.stato === 'incassata' || mov.stato === 'pagata' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {mov.stato === 'incassata' || mov.stato === 'pagata' ? 'OK' : 'Attesa'}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              {mov.booking_id && (
                                <Link to={`/admin/bookings/${mov.booking_id}`}>
                                  <Button variant="ghost" size="sm">
                                    <ExternalLink className="w-3 h-3" />
                                  </Button>
                                </Link>
                              )}
                              {mov.documento_id && (
                                <a href={mov.documento_id} target="_blank" rel="noopener noreferrer">
                                  <Button variant="ghost" size="sm">
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-100 border-t-2">
                        <tr>
                          <td colSpan="5" className="p-3 font-semibold text-gray-700">TOTALI PERIODO</td>
                          <td className="p-3 text-right font-bold text-emerald-600">
                            +{formatCurrency(data.totale_entrate)}
                          </td>
                          <td className="p-3 text-right font-bold text-red-600">
                            -{formatCurrency(data.totale_uscite)}
                          </td>
                          <td className={`p-3 text-right font-bold text-lg ${data.saldo_finale >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(data.saldo_finale)}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-2">
              {data.movimenti.map((mov, idx) => (
                <Card key={mov.id || idx} className={`overflow-hidden border-l-4 ${
                  mov.tipo_movimento === 'entrata' ? 'border-l-emerald-500' : 'border-l-red-500'
                }`}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="text-xs text-gray-500">{formatDate(mov.data)}</div>
                        <div className="font-medium text-gray-800 line-clamp-2">{mov.descrizione}</div>
                      </div>
                      <div className="text-right ml-2">
                        {mov.tipo_movimento === 'entrata' ? (
                          <div className="font-bold text-emerald-600">+{formatCurrency(mov.entrata)}</div>
                        ) : (
                          <div className="font-bold text-red-600">-{formatCurrency(mov.uscita)}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 capitalize">{mov.categoria}</span>
                        {mov.canale && <span className="text-blue-600">• {mov.canale}</span>}
                      </div>
                      <div className={`font-semibold ${mov.saldo_progressivo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        = {formatCurrency(mov.saldo_progressivo)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Mobile Total Footer */}
              <Card className="bg-gray-100">
                <CardContent className="p-4">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-xs text-gray-500">Entrate</div>
                      <div className="font-bold text-emerald-600">+{formatCurrency(data.totale_entrate)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Uscite</div>
                      <div className="font-bold text-red-600">-{formatCurrency(data.totale_uscite)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Saldo</div>
                      <div className={`font-bold text-lg ${data.saldo_finale >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(data.saldo_finale)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="px-4 py-2 text-sm">
              Pagina {pagination.page} di {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
